import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../supabase'
import AddressAutocomplete from './AddressAutocomplete'
import InfoTooltip from './shared/InfoTooltip'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', danger: '#B03020', success: '#2E6B4A',
  dangerBg: '#FDECEA', successBg: '#EBF5EF',
}
const f = 'Georgia, serif'

const propertyTypes = [
  { id: 'single_family', label: 'Single Family Home' },
  { id: 'condo', label: 'Condo / Townhome' },
  { id: 'multi_family', label: 'Multi-Family' },
  { id: 'mobile', label: 'Mobile Home' },
  { id: 'vacation', label: 'Vacation Home' },
  { id: 'other', label: 'Other' },
]

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: currentYear - 1899 }, (_, i) => currentYear - i)

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: C.bg,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 1000, padding: '24px 16px 80px', fontFamily: f,
    overflowY: 'auto',
  },
  card: {
    background: C.card, borderRadius: '16px', border: `1px solid ${C.border}`,
    boxShadow: '0 8px 40px rgba(44,29,20,0.12)',
    width: '100%', maxWidth: '480px', padding: '28px 24px',
  },
  logo: { fontSize: '22px', fontWeight: '700', color: C.brown, marginBottom: '4px' },
  headline: { fontSize: '20px', fontWeight: '700', color: C.brown, marginBottom: '6px' },
  sub: { fontSize: '14px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '28px', lineHeight: 1.5 },
  label: {
    fontSize: '10px', color: C.muted, letterSpacing: '1.5px',
    textTransform: 'uppercase', marginBottom: '5px', display: 'block',
    fontFamily: 'Calibri, sans-serif',
  },
  input: (focused) => ({
    width: '100%', background: C.bg,
    border: `1px solid ${focused ? C.amber : C.border}`,
    borderRadius: '8px', padding: '11px 14px', fontSize: '16px',
    fontFamily: f, color: C.brown, boxSizing: 'border-box',
    outline: 'none', marginBottom: '14px', transition: 'border-color 0.2s',
    WebkitUserSelect: 'text', userSelect: 'text', touchAction: 'manipulation',
    WebkitAppearance: 'none',
  }),
  select: (focused) => ({
    width: '100%', background: C.bg,
    border: `1px solid ${focused ? C.amber : C.border}`,
    borderRadius: '8px', padding: '11px 14px', fontSize: '16px',
    fontFamily: f, color: C.brown, boxSizing: 'border-box',
    outline: 'none', marginBottom: '14px', cursor: 'pointer',
    appearance: 'none', WebkitAppearance: 'none', touchAction: 'manipulation',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%239A7B68' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center',
    paddingRight: '36px',
  }),
  row: { display: 'flex', gap: '12px' },
  field: { flex: 1 },
  divider: { borderTop: `1px solid ${C.border}`, margin: '20px 0' },
  sectionLabel: {
    fontSize: '11px', color: C.muted, letterSpacing: '2px',
    textTransform: 'uppercase', marginBottom: '14px',
    fontFamily: 'Calibri, sans-serif',
  },
  btn: {
    width: '100%', background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none', borderRadius: '10px', padding: '15px',
    color: C.card, fontSize: '14px', fontWeight: '700',
    letterSpacing: '2px', textTransform: 'uppercase',
    cursor: 'pointer', fontFamily: f, marginTop: '8px',
  },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  alert: (type) => ({
    borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
    fontSize: '13px', fontFamily: 'Calibri, sans-serif',
    background: type === 'error' ? C.dangerBg : C.successBg,
    border: `1px solid ${type === 'error' ? C.danger : C.success}44`,
    color: type === 'error' ? C.danger : C.success,
  }),
  skipLink: {
    display: 'block', textAlign: 'center', marginTop: '16px',
    fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif',
    cursor: 'pointer', textDecoration: 'underline',
  },
  skipBtn: {
    width: '100%', background: 'transparent',
    border: `1px solid ${C.border}`, borderRadius: '10px',
    padding: '14px', color: C.muted, fontSize: '14px',
    fontFamily: 'Calibri, sans-serif', cursor: 'pointer',
    marginTop: '10px', letterSpacing: '0.3px',
  },
}

export default function HomeSetup({ session, onComplete, isAddingProperty = false }) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    nickname: '', address: '', city: '', state: '', zip: '',
    property_type: '', year_built: '', bedrooms: '', bathrooms: '', sqft: '',
    latitude: null, longitude: null, google_place_id: null,
  })
  const [loading, setLoading] = useState(false)
  const [alert, setAlert] = useState(null)
  const [focus, setFocus] = useState('')

  const setF = (field) => (e) => {
    setForm(p => ({ ...p, [field]: e.target.value }))
    setAlert(null)
  }

  const handleSave = async () => {
    if (!form.address.trim()) { setAlert({ type: 'error', msg: 'Street address is required.' }); return }

    setLoading(true)
    setAlert(null)

    try {
      const { data: existing } = await supabase
        .from('properties').select('id').eq('user_id', session.user.id).eq('is_primary', true)

      const isPrimary = !isAddingProperty && (!existing || existing.length === 0)

      const { data: prop, error } = await supabase.from('properties').insert({
        user_id: session.user.id,
        nickname: form.nickname.trim() || (isPrimary ? 'My Home' : 'Property'),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim() || null,
        zip: form.zip.trim() || null,
        property_type: form.property_type,
        year_built: form.year_built || null,
        bedrooms: form.bedrooms || null,
        bathrooms: form.bathrooms || null,
        sqft: form.sqft ? parseInt(form.sqft) : null,
        is_primary: isPrimary,
        latitude: form.latitude,
        longitude: form.longitude,
        google_place_id: form.google_place_id,
      }).select().single()

      if (error) throw error

      await supabase.from('profiles').upsert({
        id: session.user.id,
        first_name: session.user.user_metadata?.first_name || null,
        last_name: session.user.user_metadata?.last_name || null,
        phone: session.user.user_metadata?.phone || null,
      }, { onConflict: 'id', ignoreDuplicates: false })

      // Create homes + home_members if none exists for this user yet
      const { data: existingMembership } = await supabase
        .from('home_members').select('home_id').eq('user_id', session.user.id).limit(1).maybeSingle()
      if (!existingMembership) {
        const { data: newHome } = await supabase.from('homes').insert({
          created_by: session.user.id,
          address: form.address.trim(),
          city: form.city.trim(),
          state: form.state.trim() || null,
          zip: form.zip.trim() || null,
          year_built: form.year_built || null,
          property_type: form.property_type || null,
          latitude: form.latitude,
          longitude: form.longitude,
          google_place_id: form.google_place_id,
          onboarding_complete: true,
        }).select().single()
        if (newHome) {
          await supabase.from('home_members').insert({ home_id: newHome.id, user_id: session.user.id, role: 'owner' })
          supabase.functions.invoke('lookup-emergency-services', { body: { home_id: newHome.id } }).catch(() => {})
        }
      } else {
        // Home already created (e.g. by Auth.js) — update it with address and mark setup done
        await supabase.from('homes').update({
          address: form.address.trim(),
          city: form.city.trim(),
          state: form.state.trim() || null,
          zip: form.zip.trim() || null,
          year_built: form.year_built || null,
          property_type: form.property_type || null,
          latitude: form.latitude,
          longitude: form.longitude,
          google_place_id: form.google_place_id,
          onboarding_complete: true,
        }).eq('id', existingMembership.home_id)
      }

      onComplete(prop.id)
    } catch (err) {
      setAlert({ type: 'error', msg: 'Error saving. Please try again.' })
      setLoading(false)
    }
  }

  return (
    <div style={S.overlay}>
      <div style={S.card}>
        <div style={S.logo}>Casavio</div>
        <div style={S.headline}>
          {isAddingProperty ? 'Add a Property' : 'Welcome — set up your home'}
        </div>
        {isAddingProperty ? (
          <div style={S.sub}>Enter the details for your new property.</div>
        ) : (
          <div style={{ marginBottom: '24px', paddingTop: '4px' }}>
            {[
              { icon: '🏠', key: 'homeSetup.benefit1' },
              { icon: '📋', key: 'homeSetup.benefit2' },
              { icon: '🔧', key: 'homeSetup.benefit3' },
            ].map(({ icon, key }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <span style={{ fontSize: '20px', width: '28px', flexShrink: 0, textAlign: 'center' }}>{icon}</span>
                <span style={{ fontSize: '14px', color: C.brown, fontFamily: 'Calibri, sans-serif', lineHeight: 1.4 }}>{t(key)}</span>
              </div>
            ))}
            <div style={{ fontSize: '12px', color: C.muted, fontStyle: 'italic', marginTop: '12px' }}>{t('homeSetup.canUpdateLater')}</div>
          </div>
        )}

        {alert && <div style={S.alert(alert.type)}>{alert.msg}</div>}

        {isAddingProperty && (
          <>
            <label style={S.label}>Property Nickname (optional)</label>
            <input
              style={S.input(focus === 'nickname')}
              value={form.nickname}
              onChange={setF('nickname')}
              onFocus={() => setFocus('nickname')}
              onBlur={() => setFocus('')}
              placeholder='e.g. Beach House, Rental Unit'
              autoComplete="off"
            />
          </>
        )}

        <div style={S.sectionLabel}>Address</div>

        <label style={S.label}>Street Address *</label>
        <AddressAutocomplete
          value={form.address}
          onChange={(v) => { setForm(p => ({ ...p, address: v })); setAlert(null) }}
          onSelect={({ address, city, state, zip, latitude, longitude, placeId }) =>
            setForm(p => ({ ...p, address: address || p.address, city, state, zip, latitude: latitude ?? null, longitude: longitude ?? null, google_place_id: placeId ?? null }))
          }
          placeholder="123 Main St"
          inputStyle={S.input(false)}
        />

        <div style={S.row}>
          <div style={{ ...S.field, flex: 2 }}>
            <label style={S.label}>City *</label>
            <input
              style={S.input(focus === 'city')}
              value={form.city}
              onChange={setF('city')}
              onFocus={() => setFocus('city')}
              onBlur={() => setFocus('')}
              placeholder='City'
              autoComplete="address-level2"
            />
          </div>
          <div style={S.field}>
            <label style={S.label}>State</label>
            <input
              style={S.input(focus === 'state')}
              value={form.state}
              onChange={setF('state')}
              onFocus={() => setFocus('state')}
              onBlur={() => setFocus('')}
              placeholder='CA'
              maxLength={2}
              autoComplete="address-level1"
            />
          </div>
          <div style={S.field}>
            <label style={S.label}>ZIP</label>
            <input
              style={S.input(focus === 'zip')}
              value={form.zip}
              onChange={setF('zip')}
              onFocus={() => setFocus('zip')}
              onBlur={() => setFocus('')}
              placeholder='00000'
              maxLength={10}
              autoComplete="postal-code"
            />
          </div>
        </div>

        <div style={S.divider} />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '14px' }}>
          <div style={{ ...S.sectionLabel, marginBottom: 0 }}>Property Details</div>
          <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>No worries if you don't know — you can add these later.</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
          <label style={{ ...S.label, marginBottom: 0 }}>Property Type *</label>
          <InfoTooltip text={t('tooltips.propertyType')} />
        </div>
        <select
          style={S.select(focus === 'property_type')}
          value={form.property_type}
          onChange={setF('property_type')}
          onFocus={() => setFocus('property_type')}
          onBlur={() => setFocus('')}
        >
          <option value=''>Select type...</option>
          {propertyTypes.map(pt => (
            <option key={pt.id} value={pt.id}>{pt.label}</option>
          ))}
        </select>

        <div style={S.row}>
          <div style={S.field}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
              <label style={{ ...S.label, marginBottom: 0 }}>Year Built</label>
              <InfoTooltip text={t('tooltips.yearBuilt')} />
            </div>
            <select
              style={S.select(focus === 'year_built')}
              value={form.year_built}
              onChange={setF('year_built')}
              onFocus={() => setFocus('year_built')}
              onBlur={() => setFocus('')}
            >
              <option value=''>Year...</option>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={S.field}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
              <label style={{ ...S.label, marginBottom: 0 }}>Bedrooms</label>
              <InfoTooltip text={t('tooltips.bedsAndBaths')} />
            </div>
            <select
              style={S.select(focus === 'bedrooms')}
              value={form.bedrooms}
              onChange={setF('bedrooms')}
              onFocus={() => setFocus('bedrooms')}
              onBlur={() => setFocus('')}
            >
              <option value=''>Beds...</option>
              {['Studio', '1', '2', '3', '4', '5', '6+'].map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div style={S.field}>
            <label style={S.label}>Bathrooms</label>
            <select
              style={S.select(focus === 'bathrooms')}
              value={form.bathrooms}
              onChange={setF('bathrooms')}
              onFocus={() => setFocus('bathrooms')}
              onBlur={() => setFocus('')}
            >
              <option value=''>Baths...</option>
              {['1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5+'].map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
          <label style={{ ...S.label, marginBottom: 0 }}>Square Footage</label>
          <InfoTooltip text={t('tooltips.sqft')} />
        </div>
        <input
          style={S.input(focus === 'sqft')}
          value={form.sqft}
          onChange={setF('sqft')}
          onFocus={() => setFocus('sqft')}
          onBlur={() => setFocus('')}
          placeholder='e.g. 1800'
          type='number'
          autoComplete="off"
        />

        <button
          style={{ ...S.btn, ...(loading ? S.btnDisabled : {}) }}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : isAddingProperty ? 'Add Property' : 'Get Started'}
        </button>

        {isAddingProperty ? (
          <span style={S.skipLink} onClick={() => onComplete(null)}>Cancel</span>
        ) : (
          <button style={S.skipBtn} onClick={() => onComplete(null)}>
            Skip for now — I'll add my address later
          </button>
        )}
      </div>
    </div>
  )
}
